import React, { useEffect, useState } from "react";
import { Button, Form, Row, Col, Alert } from "react-bootstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import ConfirmModal, { MODAL_SIZES } from "../../components/ConfirmModal";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import callApi from "../../helpers/CallApi";
import { createBankAccount, updateAccount } from "../../helpers/API";
import { toast } from "react-toastify";
import {
  ACCOUNT_HOLDER_NAME_REGEX,
  ACCOUNT_NUMBER_REGEX,
  IFSC_CODE_REGEX,
} from "../../helpers/regex";

const BankAccountModal = ({ show, toggle, data = null, handleRefresh }) => {
  const initialValues = {
    bankName: "",
    branch: "",
    accountHolderName: "",
    accountNumber: "",
    ifsc: "",
    openingBalance: "",
  };
  const [formData, setFormData] = useState(initialValues);
  const { t } = useTranslation();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formDataToSave, setFormDataToSave] = useState(null);
  const [modelType, setModelType] = useState("add");

  useEffect(() => {
    if (data) {
      const { bankName, branch, accountHolderName, accountNumber, ifsc } = data;
      setFormData({ bankName, branch, accountHolderName, accountNumber, ifsc });
      setModelType("update");
    }
  }, [data]);

  // Validation Schema
  const validationSchema = Yup.object({
    bankName: Yup.string().required(
      t("account.bankAccount.validation.bankNameRequired")
    ),
    branch: Yup.string()
      .min(2, t("account.bankAccount.validation.branchMinLength"))
      .max(50, t("account.bankAccount.validation.branchMaxLength"))
      .required(t("account.bankAccount.validation.branchRequired")),
    accountHolderName: Yup.string()
      .min(3, t("account.bankAccount.validation.accountHolderMinLength"))
      .max(100, t("account.bankAccount.validation.accountHolderMaxLength"))
      .matches(
        ACCOUNT_HOLDER_NAME_REGEX,
        t("account.bankAccount.validation.accountHolderInvalid")
      )
      .required(t("account.bankAccount.validation.accountHolderRequired")),
    accountNumber: Yup.string()
      .matches(
        ACCOUNT_NUMBER_REGEX,
        t("account.bankAccount.validation.accountNumberInvalid")
      )
      .required(t("account.bankAccount.validation.accountNumberRequired")),
    ifsc: Yup.string()
      .matches(
        IFSC_CODE_REGEX,
        t("account.bankAccount.validation.ifscCodeInvalid")
      )
      .required(t("account.bankAccount.validation.ifscCodeRequired")),
    ...(!data
      ? {
        openingBalance: Yup.number()
          .min(0, t("account.bankAccount.validation.openingBalanceMin"))
          .max(
            999999999.99,
            t("account.bankAccount.validation.openingBalanceMax")
          )
          .required(
            t("account.bankAccount.validation.openingBalanceRequired")
          ),
      }
      : {}),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    setFormDataToSave(values);
    setShowConfirmation(true);
    setSubmitting(false);
  };

  const handleConfirmedSave = () => {
    callApi({
      callback: data ? updateAccount : createBankAccount,
      payload: [formDataToSave, data?.id],
      onSuccess: ({ message }) => {
        toast.success(
          message || data
            ? t("account.bankAccount.messages.updateSuccess")
            : t("account.bankAccount.messages.createSuccess")
        );
        handleRefresh();
        handleClose();
      },
      onError: (message) => {
        toast.error(message || t("account.bankAccount.messages.error"));
      },
    });
  };

  const handleClose = () => {
    setShowConfirmation(false);
    setFormDataToSave(null);
    toggle();
    // setFormData(initialValues);
  };
  const getBankDetails = (val) => {
    const api = `https://ifsc.razorpay.com/${val}`;
    fetch(api)
      .then((res) => res.json())
      .then((data) => {
        if (data?.BRANCH) {
          setFormData({
            ...formData,
            branch: data?.BRANCH,
            bankName: data?.BANK,
            ifsc: data?.IFSC,
          });
        }
      })
      .catch((err) => console.log(err));
  };

  return (
    <>
      <Modal isOpen={show} toggle={handleClose} size="lg" centered>
        <Formik
          initialValues={formData}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <>
              <ModalHeader toggle={handleClose}>
                {t("account.bankAccount.modal.title")}
              </ModalHeader>
              <ModalBody>
                <Form noValidate onSubmit={handleSubmit}>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="required">
                          {t("account.bankAccount.form.ifscCode")}{" "}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="ifsc"
                          value={values.ifsc}
                          onChange={(e) => {
                            const upperValue = e.target.value.toUpperCase();
                            getBankDetails(upperValue);
                            setFieldValue("ifsc", upperValue);
                          }}
                          onBlur={handleBlur}
                          isInvalid={touched.ifsc && !!errors.ifsc}
                          placeholder="ABCD0123456"
                          style={{ textTransform: "uppercase" }}
                        />
                        <ErrorMessage
                          name="ifsc"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          {t("account.bankAccount.form.bankName")}{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          disabled
                          type="text"
                          name="bankName"
                          value={values.bankName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.bankName && !!errors.bankName}
                          placeholder={t(
                            "account.bankAccount.form.bankNamePlaceholder"
                          )}
                        />

                        <ErrorMessage
                          name="bankName"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="required">
                          {t("account.bankAccount.form.branch")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="branch"
                          disabled
                          value={values.branch}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.branch && !!errors.branch}
                          placeholder={t(
                            "account.bankAccount.form.branchPlaceholder"
                          )}
                        />
                        <ErrorMessage
                          name="branch"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="required">
                          {t("account.bankAccount.form.accountHolderName")}{" "}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="accountHolderName"
                          value={values.accountHolderName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={
                            touched.accountHolderName &&
                            !!errors.accountHolderName
                          }
                          placeholder={t(
                            "account.bankAccount.form.accountHolderPlaceholder"
                          )}
                        />
                        <ErrorMessage
                          name="accountHolderName"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="required">
                          {t("account.bankAccount.form.accountNumber")}{" "}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="accountNumber"
                          value={values.accountNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={
                            touched.accountNumber && !!errors.accountNumber
                          }
                          placeholder={t(
                            "account.bankAccount.form.accountNumberPlaceholder"
                          )}
                        />
                        <ErrorMessage
                          name="accountNumber"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </Form.Group>
                    </Col>

                    {!data && (
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="required">
                            {t("account.bankAccount.form.openingBalance")}{" "}
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="openingBalance"
                            value={values.openingBalance}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touched.openingBalance && !!errors.openingBalance
                            }
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                          />
                          <ErrorMessage
                            name="openingBalance"
                            component="div"
                            className="text-danger small mt-1"
                          />
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                </Form>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={handleClose}>
                  {t("account.common.cancel")}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t("account.common.saving")
                    : data
                      ? t("account.common.update")
                      : t("account.common.save")}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </Modal>

      <ConfirmModal
        isOpen={showConfirmation}
        toggle={() => setShowConfirmation(false)}
        onConfirm={handleConfirmedSave}
        variant={"SAVE"}
        title={t(`account.bankAccount.confirmation.${modelType}.title`)}
        message={t(`account.bankAccount.confirmation.${modelType}.message`)}
        size={MODAL_SIZES.MEDIUM}
      // showIcon={true}
      >
        <div>
          <Alert color="warning" className="mb-3">
            <i className="ri-alert-line me-2"></i>
            {t(`account.bankAccount.confirmation.${modelType}.message`)}
          </Alert>
          <p className="mb-0">
            {t(`account.bankAccount.confirmation.${modelType}.note`)}
          </p>
        </div>
      </ConfirmModal>
    </>
  );
};

export default BankAccountModal;
